import AdminShell from "@/components/AdminShell";
/**
 * KISSED University · Admin Review Queue (super_admin only).
 * Route: /admin/university
 *
 * Features:
 *  - Filterable list (Program, Status, Years, Licensed, search)
 *  - Detail Sheet: applicant snapshot, qualification, audit trail
 *  - Decisions: accept / conditionally_accept / waitlist / decline /
 *               mark_under_review / mark_enrolled (with reason)
 *  - Inline notes editor (PATCH /notes)
 *  - Health summary tiles
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  GraduationCap, Loader2, RefreshCw, ChevronLeft, Search as SearchIcon,
  CheckCircle2, AlertCircle, Clock, XCircle, Hourglass, Award, BookOpen, CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { TopNav } from "@/components/Layout";
import { adminAuth } from "@/lib/api";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";

const STATUSES = [
  "payment_pending", "submitted", "under_review", "accepted", "conditionally_accepted",
  "waitlisted", "declined", "enrolled",
];
const STATUS_LABEL = {
  payment_pending: "Awaiting payment",
  submitted: "Submitted",
  under_review: "Under review",
  accepted: "Accepted",
  conditionally_accepted: "Conditionally accepted",
  waitlisted: "Waitlisted",
  declined: "Declined",
  enrolled: "Enrolled",
};
const STATUS_BADGE = {
  payment_pending: "bg-amber-50 text-amber-800 border-amber-200",
  submitted: "bg-slate-100 text-slate-700 border-slate-200",
  under_review: "bg-amber-50 text-amber-800 border-amber-200",
  accepted: "bg-emerald-50 text-emerald-800 border-emerald-200",
  conditionally_accepted: "bg-teal-50 text-teal-800 border-teal-200",
  waitlisted: "bg-purple-50 text-purple-900 border-purple-200",
  declined: "bg-red-50 text-red-700 border-red-200",
  enrolled: "bg-indigo-50 text-indigo-800 border-indigo-200",
};
const STATUS_ICON = {
  payment_pending: CreditCard,
  submitted: Clock,
  under_review: Hourglass,
  accepted: CheckCircle2,
  conditionally_accepted: CheckCircle2,
  waitlisted: AlertCircle,
  declined: XCircle,
  enrolled: Award,
};

const ENROLLMENT_LABEL = {
  pay_in_full: "Pay in Full",
  pay_per_course: "Pay Per Course",
  undecided: "Undecided",
};

const FEE_STATUS_BADGE = {
  Pending: "bg-amber-50 text-amber-800 border-amber-200",
  Paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Waived: "bg-slate-100 text-slate-700 border-slate-200",
  Failed: "bg-red-50 text-red-700 border-red-200",
};

const PAYMENT_ACTIONS = [
  { key: "mark_paid", label: "Mark Paid", className: "bg-emerald-700 hover:bg-emerald-800 text-white" },
  { key: "mark_waived", label: "Mark Waived", className: "bg-slate-700 hover:bg-slate-800 text-white" },
  { key: "mark_failed", label: "Mark Failed", className: "bg-red-700 hover:bg-red-800 text-white" },
  { key: "mark_pending", label: "Reset to Pending", className: "bg-amber-600 hover:bg-amber-700 text-white" },
];

const DECISION_BUTTONS = [
  { key: "accept", label: "Accept", className: "bg-emerald-700 hover:bg-emerald-800 text-white" },
  { key: "conditionally_accept", label: "Conditionally Accept", className: "bg-teal-700 hover:bg-teal-800 text-white" },
  { key: "waitlist", label: "Waitlist", className: "bg-purple-700 hover:bg-purple-800 text-white" },
  { key: "decline", label: "Decline", className: "bg-red-700 hover:bg-red-800 text-white" },
  { key: "mark_under_review", label: "Mark Under Review", className: "bg-amber-600 hover:bg-amber-700 text-white" },
  { key: "mark_enrolled", label: "Mark Enrolled", className: "bg-indigo-700 hover:bg-indigo-800 text-white" },
];

function StatusBadge({ status }) {
  const Icon = STATUS_ICON[status] || Clock;
  const cls = STATUS_BADGE[status] || STATUS_BADGE.submitted;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 border ${cls}`}
    >
      <Icon className="w-3 h-3" />
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function QualPill({ qualification }) {
  if (!qualification) return null;
  const tier = qualification.tier || "needs_more_info";
  const score = qualification.score ?? 0;
  const map = {
    priority_review: "bg-emerald-50 text-emerald-800 border-emerald-200",
    auto_review: "bg-slate-100 text-slate-700 border-slate-200",
    needs_more_info: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <span
      data-testid={`qual-tier-${tier}`}
      className={`text-xs rounded-full px-2 py-0.5 border ${map[tier] || map.needs_more_info}`}
      title={`Score: ${score}`}
    >
      {tier.replace(/_/g, " ")} · {score}
    </span>
  );
}

function HealthTile({ label, value, testid }) {
  return (
    <div className="kcis-card p-4" data-testid={testid}>
      <div className="kcis-overline">{label}</div>
      <div className="font-display text-2xl text-slate-900 mt-1">{value}</div>
    </div>
  );
}

const PROGRAMS_PLACEHOLDER = []; // populated from /options

export default function AdminUniversity() {
  const navigate = useNavigate();
  const [opts, setOpts] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Filters
  const [fProgram, setFProgram] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fYears, setFYears] = useState("all");
  const [fLicensed, setFLicensed] = useState("all");
  const [fEnroll, setFEnroll] = useState("all");
  const [fFeeStatus, setFFeeStatus] = useState("all");
  const [q, setQ] = useState("");

  // Selection / drawer
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    if (!adminAuth.isSuperAdmin()) {
      toast.error("Super admin access required.");
      navigate("/admin");
      return;
    }
    universityApi.options().then((r) => setOpts(r.data)).catch(() => setOpts(null));
  }, [navigate]);

  const fetchHealth = async () => {
    try {
      const r = await universityApi.adminHealth();
      setHealth(r.data);
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.message("University module is currently disabled.");
      } else if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session expired. Please sign in again.");
        adminAuth.clear();
        navigate("/admin/login");
      }
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (fProgram !== "all") params.program = fProgram;
      if (fStatus !== "all") params.status = fStatus;
      if (fYears !== "all") params.years = fYears;
      if (fLicensed !== "all") params.licensed = fLicensed;
      if (fEnroll !== "all") params.enrollment_preference = fEnroll;
      if (fFeeStatus !== "all") params.application_fee_status = fFeeStatus;
      if (q.trim()) params.q = q.trim();
      const r = await universityApi.adminList(params);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.message("University module is currently disabled.");
        setItems([]); setTotal(0);
      } else {
        toast.error(formatApiError(err, "Failed to load applications"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHealth(); fetchList(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [page, fProgram, fStatus, fYears, fLicensed, fEnroll, fFeeStatus]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDrawerOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await universityApi.adminDetail(id);
      setDetail(r.data);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load application"));
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([fetchHealth(), fetchList()]);
    if (selectedId) {
      try {
        const r = await universityApi.adminDetail(selectedId);
        setDetail(r.data);
      } catch { /* ignore */ }
    }
  };

  const programs = opts?.program_options || PROGRAMS_PLACEHOLDER;
  const yearsOptions = opts?.years_in_practice || [];

  const showingRange = useMemo(() => {
    if (!total) return "0";
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(total, page * pageSize);
    return `${start}–${end} of ${total}`;
  }, [page, total]);

  return (
    <AdminShell door="university" title="Admissions">
      <TopNav
        right={
          <div className="flex items-center gap-1">
            <Link to="/admin/university/enrollments">
              <Button variant="ghost" className="rounded-full text-slate-700" data-testid="admin-enrollments-link">
                <GraduationCap className="h-4 w-4 mr-1" /> Enrollments
              </Button>
            </Link>
            <Link to="/admin/university/catalog">
              <Button variant="ghost" className="rounded-full text-slate-700" data-testid="admin-catalog-link">
                <BookOpen className="h-4 w-4 mr-1" /> Catalog
              </Button>
            </Link>
            <Link to="/admin">
              <Button variant="ghost" className="rounded-full text-slate-700" data-testid="admin-back-link">
                <ChevronLeft className="h-4 w-4 mr-1" /> Admin home
              </Button>
            </Link>
          </div>
        }
      />
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="kcis-overline text-purple-900">KISSED University</div>
            <h1 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2 flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-purple-900" /> Admissions Review Queue
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              Review, decide, and track university admissions applications.
            </p>
          </div>
          <Button variant="outline" onClick={refreshAll} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Health tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-8">
          <HealthTile testid="health-total" label="Total applications" value={health?.total_applications ?? "—"} />
          <HealthTile testid="health-pending-payment" label="Awaiting payment" value={health?.by_status?.payment_pending ?? "—"} />
          <HealthTile testid="health-submitted" label="Submitted" value={health?.by_status?.submitted ?? "—"} />
          <HealthTile testid="health-under-review" label="Under review" value={health?.by_status?.under_review ?? "—"} />
          <HealthTile testid="health-accepted" label="Accepted + Cond." value={
            (health?.by_status?.accepted ?? 0) + (health?.by_status?.conditionally_accepted ?? 0)
          } />
        </div>

        {/* Filters */}
        <div className="mt-8 kcis-card p-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2">
              <Label className="text-xs">Search name or email</Label>
              <div className="relative mt-1">
                <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="filter-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); fetchList(); } }}
                  placeholder="Search…"
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Program</Label>
              <Select value={fProgram} onValueChange={(v) => { setPage(1); setFProgram(v); }}>
                <SelectTrigger data-testid="filter-program" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All programs</SelectItem>
                  {programs.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={fStatus} onValueChange={(v) => { setPage(1); setFStatus(v); }}>
                <SelectTrigger data-testid="filter-status" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Years in practice</Label>
              <Select value={fYears} onValueChange={(v) => { setPage(1); setFYears(v); }}>
                <SelectTrigger data-testid="filter-years" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  {yearsOptions.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Licensed</Label>
              <Select value={fLicensed} onValueChange={(v) => { setPage(1); setFLicensed(v); }}>
                <SelectTrigger data-testid="filter-licensed" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="yes">Has license #</SelectItem>
                  <SelectItem value="no">No license #</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Enrollment Pref.</Label>
              <Select value={fEnroll} onValueChange={(v) => { setPage(1); setFEnroll(v); }}>
                <SelectTrigger data-testid="filter-enrollment" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="pay_in_full">Pay in Full</SelectItem>
                  <SelectItem value="pay_per_course">Pay Per Course</SelectItem>
                  <SelectItem value="undecided">Undecided</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fee Status</Label>
              <Select value={fFeeStatus} onValueChange={(v) => { setPage(1); setFFeeStatus(v); }}>
                <SelectTrigger data-testid="filter-fee-status" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Waived">Waived</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 kcis-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="text-sm text-slate-600">Showing {showingRange}</div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="page-prev"
              >Prev</Button>
              <span className="text-xs text-slate-600">Page {page}</span>
              <Button
                variant="outline" size="sm"
                disabled={loading || page * pageSize >= total}
                onClick={() => setPage((p) => p + 1)}
                data-testid="page-next"
              >Next</Button>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Applicant</TableHead>
                <TableHead>Primary Program</TableHead>
                <TableHead>Enrollment Pref.</TableHead>
                <TableHead>Fee</TableHead>
                <TableHead>Years</TableHead>
                <TableHead>Qualification</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="w-[120px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin inline text-slate-400" />
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500" data-testid="empty-row">
                  No applications match these filters.
                </TableCell></TableRow>
              ) : items.map((row) => (
                <TableRow key={row.id} data-testid={`app-row-${row.id}`}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{row.full_name}</div>
                    <div className="text-xs text-slate-500">{row.email}</div>
                    {row.public_application_id ? (
                      <div
                        className="text-[11px] font-mono text-purple-900 mt-0.5"
                        data-testid={`row-public-id-${row.id}`}
                      >
                        {row.public_application_id}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-800 leading-snug">
                      {row.primary_program || (row.programs_of_interest || [])[0] || "—"}
                    </div>
                    {row.secondary_program && (
                      <div className="text-[11px] text-slate-500 mt-0.5">+ {row.secondary_program}</div>
                    )}
                    {row.specialization_track && (
                      <div className="text-[11px] text-purple-900 mt-0.5">★ {row.specialization_track}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-700">
                    {ENROLLMENT_LABEL[row.enrollment_preference] || "—"}
                  </TableCell>
                  <TableCell>
                    <span
                      data-testid={`fee-badge-${row.id}`}
                      className={`inline-flex items-center text-xs rounded-full px-2 py-0.5 border ${FEE_STATUS_BADGE[row.application_fee_status] || FEE_STATUS_BADGE.Pending}`}
                    >
                      {row.application_fee_status || "Pending"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{row.years_in_practice || "—"}</TableCell>
                  <TableCell><QualPill qualification={row.qualification} /></TableCell>
                  <TableCell><StatusBadge status={row.current_status} /></TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline"
                      onClick={() => openDetail(row.id)}
                      data-testid={`review-btn-${row.id}`}>Review</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <ApplicationDrawer
        open={drawerOpen}
        onOpenChange={(o) => { setDrawerOpen(o); if (!o) { setSelectedId(null); setDetail(null); } }}
        detail={detail}
        loading={detailLoading}
        onChange={refreshAll}
      />
    </AdminShell>
  );
}

function ApplicationDrawer({ open, onOpenChange, detail, loading, onChange }) {
  const [reason, setReason] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [notes, setNotes] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [notesBusy, setNotesBusy] = useState(false);
  const [squareRef, setSquareRef] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [payBusyKey, setPayBusyKey] = useState("");

  useEffect(() => {
    setReason(detail?.decision_reason || "");
    setPaymentLink(detail?.payment_link_override || "");
    setNotes(detail?.admissions_notes || "");
    setSquareRef(detail?.square_payment_reference || "");
    setPaymentNotes(detail?.payment_notes || "");
  }, [detail?.id]); // reset when a new application is opened

  if (!detail && !loading) return null;

  const decide = async (decision) => {
    if (!detail?.id) return;
    setBusyKey(decision);
    try {
      await universityApi.adminDecide(detail.id, {
        decision,
        reason,
        admissions_notes: notes,
        payment_link_override: paymentLink || null,
      });
      toast.success(`Decision recorded: ${decision.replace(/_/g, " ")}`);
      await onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Decision failed"));
    } finally {
      setBusyKey("");
    }
  };

  const convertToEnrollment = async () => {
    if (!detail?.id) return;
    setBusyKey("convert");
    try {
      const r = await universityApi.adminConvertToEnrollment(detail.id);
      const enr = r?.data?.enrollment;
      if (r?.data?.resumed) {
        toast.info(`Already converted — enrollment ${enr?.public_enrollment_id || enr?.id?.slice(0,8)}`);
      } else {
        toast.success(`Enrollment created: ${enr?.public_enrollment_id || enr?.id?.slice(0,8)}`);
      }
      await onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Conversion failed"));
    } finally {
      setBusyKey("");
    }
  };

  const updatePayment = async (action) => {
    if (!detail?.id) return;
    setPayBusyKey(action);
    try {
      await universityApi.adminUpdatePayment(detail.id, {
        action,
        square_payment_reference: squareRef,
        payment_notes: paymentNotes,
      });
      toast.success(`Payment status updated: ${action.replace(/_/g, " ")}`);
      await onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Payment update failed"));
    } finally {
      setPayBusyKey("");
    }
  };

  const saveNotes = async () => {
    if (!detail?.id) return;
    setNotesBusy(true);
    try {
      await universityApi.adminUpdateNotes(detail.id, notes);
      toast.success("Notes saved");
      await onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Saving notes failed"));
    } finally {
      setNotesBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="application-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-purple-900" />
            {detail?.full_name || "Loading…"}
            {detail?.public_application_id ? (
              <span
                className="font-mono text-xs font-normal text-purple-900 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5"
                data-testid="drawer-public-application-id"
              >
                {detail.public_application_id}
              </span>
            ) : null}
          </SheetTitle>
          <SheetDescription>
            {detail ? (
              <span className="flex items-center gap-2">
                <StatusBadge status={detail.current_status} />
                <QualPill qualification={detail.qualification} />
              </span>
            ) : null}
          </SheetDescription>
        </SheetHeader>

        {loading || !detail ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="mt-6 space-y-6">
            {/* Identity */}
            <Section title="Applicant">
              <KV k="Email" v={detail.email} testid="d-email" />
              <KV k="Phone" v={detail.phone} testid="d-phone" />
              <KV k="Location" v={[detail.city, detail.state_province, detail.country].filter(Boolean).join(", ") || "—"} />
              {detail.public_application_id ? (
                <KV
                  k="Application ID"
                  v={<span className="font-mono text-sm font-semibold text-purple-900">{detail.public_application_id}</span>}
                  testid="d-public-application-id"
                />
              ) : null}
              <KV k="Internal ID" v={<span className="font-mono text-xs text-slate-500">{detail.id}</span>} />
              <KV k="Submitted" v={detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"} />
              {detail.decided_at && <KV k="Decided" v={`${new Date(detail.decided_at).toLocaleString()} · ${detail.decided_by || "—"}`} />}
            </Section>

            {/* Education + Professional */}
            <Section title="Background">
              <KV k="Education" v={detail.highest_education_level || "—"} />
              <KV k="Credentials" v={(detail.credentials_held || []).join(", ") || "—"} />
              <KV k="License #" v={detail.license_number || "—"} />
              <KV k="Current Professional Status" v={detail.current_professional_status || "—"} testid="d-professional-status" />
              <KV k="Years in practice" v={detail.years_in_practice || "—"} />
              <KV k="Practice setting" v={detail.practice_setting || "—"} />
              <KV k="Employment" v={detail.employment_status || "—"} />
              <KV k="Modalities" v={(detail.current_modalities || []).join(", ") || "—"} />
              <KV k="Portfolio" v={detail.portfolio_url ? <a className="text-purple-900 underline" href={detail.portfolio_url} target="_blank" rel="noreferrer">{detail.portfolio_url}</a> : "—"} />
              <KV k="Prior KISSED alumni" v={detail.prior_kissed_alumni || "—"} />
            </Section>

            {/* Programs & readiness */}
            <Section title="Program interest & readiness">
              <KV k="Primary Program" v={detail.primary_program || "—"} testid="d-primary" />
              <KV k="Secondary Program" v={detail.secondary_program || "—"} testid="d-secondary" />
              <KV k="Specialization Track" v={detail.specialization_track || "—"} testid="d-specialization" />
              <KV k="Enrollment Preference" v={ENROLLMENT_LABEL[detail.enrollment_preference] || "—"} testid="d-enrollment" />
              <KV k="Attendance mode" v={detail.attendance_mode || "—"} />
              <KV k="Start window" v={detail.start_window || "—"} />
              <KV k="Hours/week" v={detail.hours_per_week_available || "—"} />
              <KV k="Heard from" v={detail.heard_from || "—"} />
            </Section>

            {/* Admissions Quiz context (only when present) */}
            {(detail.pathway_source || detail.quiz_recommended_program || detail.desired_start_month || detail.ce_course_interest) ? (
              <Section title="Admissions quiz / pathway">
                <KV k="Pathway source"          v={detail.pathway_source || "—"}             testid="d-pathway-source" />
                <KV k="Quiz recommendation"     v={detail.quiz_recommended_program || "—"}   testid="d-quiz-recommended" />
                <KV k="Selected program"        v={detail.selected_program || detail.primary_program || "—"} testid="d-selected-program" />
                <KV k="Desired start month"     v={detail.desired_start_month || "—"}        testid="d-desired-start-month" />
                <KV k="CE course interest"      v={detail.ce_course_interest || "—"}         testid="d-ce-course-interest" />
              </Section>
            ) : null}

            {/* Payment */}
            <Section title="Application fee payment (Square)">
              <KV k="Fee status" v={
                <span className={`inline-flex items-center text-xs rounded-full px-2 py-0.5 border ${FEE_STATUS_BADGE[detail.application_fee_status] || FEE_STATUS_BADGE.Pending}`}>
                  {detail.application_fee_status || "Pending"}
                </span>
              } testid="d-fee-status" />
              <KV k="Payment method" v={detail.application_fee_payment_method || "Square"} />
              <KV k="Amount" v={`$${Number(detail.application_fee_amount_usd || 50).toFixed(2)} USD`} />
              <KV k="Paid date" v={detail.application_fee_paid_date ? new Date(detail.application_fee_paid_date).toLocaleString() : "—"} />
              <KV k="Square reference" v={detail.square_payment_reference ? <span className="font-mono text-xs break-all">{detail.square_payment_reference}</span> : "—"} testid="d-square-ref" />
              {detail.payment_notes ? <KV k="Payment notes" v={<span className="text-xs italic">{detail.payment_notes}</span>} testid="d-payment-notes" /> : null}

              {/* Reconcile panel */}
              <div className="mt-4 rounded-xl border border-purple-200 bg-purple-50/50 p-3 space-y-2">
                <div className="text-xs uppercase tracking-wide text-purple-900 font-semibold">Manual reconciliation</div>
                <Label className="text-xs">Square payment reference (optional)</Label>
                <Input
                  data-testid="square-ref-input"
                  value={squareRef}
                  onChange={(e) => setSquareRef(e.target.value)}
                  placeholder="e.g. SQ-9F1A2B"
                />
                <Label className="text-xs">Payment notes (optional)</Label>
                <Textarea
                  data-testid="payment-notes-input"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  className="min-h-[60px] text-xs"
                  placeholder="e.g. Confirmed via Square dashboard, txn 2026-05-30."
                />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {PAYMENT_ACTIONS.map((a) => (
                    <Button
                      key={a.key}
                      disabled={!!payBusyKey}
                      onClick={() => updatePayment(a.key)}
                      data-testid={`pay-${a.key}`}
                      className={a.className}
                    >
                      {payBusyKey === a.key ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                      {a.label}
                    </Button>
                  ))}
                </div>
              </div>
            </Section>

            {/* Motivation */}
            <Section title="Motivation">
              <Para label="Why KISSED?" text={detail.why_kissed} />
              <Para label="Learning goals" text={detail.learning_goals} />
              {detail.alignment_statement ? <Para label="Alignment statement" text={detail.alignment_statement} /> : null}
            </Section>

            {/* Qualification tags */}
            {detail.qualification?.reasoning_tags?.length ? (
              <Section title="Qualification reasoning">
                <div className="flex flex-wrap gap-2">
                  {detail.qualification.reasoning_tags.map((t) => (
                    <Badge key={t} variant="secondary" className="text-[10px] font-normal">{t}</Badge>
                  ))}
                </div>
              </Section>
            ) : null}

            {/* Decision panel */}
            <Section title="Make a decision">
              {(detail.current_status === "payment_pending" || detail.application_fee_status === "Pending") && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 mb-2" data-testid="decision-blocked">
                  This application's $50 fee has not yet been reconciled. Mark the fee Paid (or Waived) in the panel above before recording a decision.
                </div>
              )}
              <Label className="text-xs">Reason / message (sent in webhook + audit; included in emails)</Label>
              <Textarea
                data-testid="decision-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 min-h-[80px]"
                placeholder="Optional rationale for the decision…"
              />
              <Label className="text-xs mt-3 block">Payment link override (optional)</Label>
              <Input
                data-testid="payment-link-override"
                value={paymentLink}
                onChange={(e) => setPaymentLink(e.target.value)}
                className="mt-1"
                placeholder="https://"
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
                {DECISION_BUTTONS.map((d) => (
                  <Button
                    key={d.key}
                    disabled={!!busyKey || detail.current_status === "payment_pending" || detail.application_fee_status === "Pending"}
                    onClick={() => decide(d.key)}
                    data-testid={`decide-${d.key}`}
                    className={d.className}
                  >
                    {busyKey === d.key ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    {d.label}
                  </Button>
                ))}
              </div>
            </Section>

            {/* Convert accepted application → enrollment (visible only for accepted / conditionally_accepted) */}
            {(detail.current_status === "accepted" || detail.current_status === "conditionally_accepted" || detail.current_status === "enrolled") && (
              <Section title="Convert to enrollment">
                <div className="rounded-xl border border-purple-100 bg-purple-50/50 p-3 text-xs text-slate-700 mb-3 space-y-1" data-testid="convert-preview">
                  <div><span className="text-slate-500">Applicant:</span> <span className="font-medium">{detail.full_name || "—"}</span></div>
                  <div><span className="text-slate-500">Email:</span> <span className="font-medium">{detail.email || "—"}</span></div>
                  <div><span className="text-slate-500">Program:</span> <span className="font-medium">{detail.primary_program || "—"}</span></div>
                  {detail.recommended_program && detail.recommended_program !== detail.primary_program ? (
                    <div><span className="text-slate-500">Recommended program:</span> <span className="font-medium">{detail.recommended_program}</span></div>
                  ) : null}
                  <div><span className="text-slate-500">Application ID:</span> <span className="font-mono">{detail.public_application_id || detail.id.slice(0,8)}</span></div>
                </div>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                  Creates a University Enrollment linked to this application. No payment links and no emails are sent.
                  Application status will be moved to <span className="font-medium">enrolled</span>. Safe to click more than once — idempotent.
                </p>
                <Button
                  onClick={convertToEnrollment}
                  disabled={!!busyKey}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="convert-to-enrollment-btn"
                >
                  {busyKey === "convert" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Convert to enrollment
                </Button>
              </Section>
            )}

            {/* Internal notes */}
            <Section title="Internal admissions notes">
              <Textarea
                data-testid="admissions-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[100px]"
                placeholder="Private notes — not sent to the applicant."
              />
              <div className="mt-2 flex justify-end">
                <Button
                  variant="outline" size="sm"
                  disabled={notesBusy}
                  onClick={saveNotes}
                  data-testid="save-notes-btn"
                >
                  {notesBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Save notes
                </Button>
              </div>
            </Section>

            {/* Audit */}
            <Section title="Audit trail">
              {(detail.audit || []).length === 0 ? (
                <div className="text-xs text-slate-500">No audit entries yet.</div>
              ) : (
                <div className="space-y-2" data-testid="audit-trail">
                  {(detail.audit || []).map((a, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 bg-white p-3 text-xs">
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-slate-800">{a.action}</div>
                        <div className="text-slate-500">{a.at ? new Date(a.at).toLocaleString() : ""}</div>
                      </div>
                      <div className="mt-1 text-slate-600">
                        <span className="text-slate-500">by</span> {a.actor || "—"}
                        {a.reason ? <> · <span className="text-slate-500">reason:</span> {a.reason}</> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="kcis-overline text-purple-900 mb-2">{title}</div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function KV({ k, v, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm" data-testid={testid}>
      <div className="text-slate-500 w-36 shrink-0">{k}</div>
      <div className="text-slate-900 text-right break-all">{v || "—"}</div>
    </div>
  );
}

function Para({ label, text }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{text}</div>
    </div>
  );
}
